/**
 * DailyTasksWidget.jsx — Phase 76 + 77
 *
 * Personal daily-task widget for the main dashboard.
 *  - Filter pill rows: project (Phase 77) + assignee.
 *  - Each task row carries an inline project chip (only daily-task-enabled
 *    projects appear) + an "hours" chip that opens a popover for inline
 *    time-entry → submits to the timesheet via the back-linked endpoint.
 *  - Anyone creates their own task; only admin / client_manager can assign.
 *  - Open tasks always show until explicitly checked off (no roll-over).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckSquare, Square, Plus, Loader2, Trash2, ListChecks, Clock, FolderKanban, Filter, LayoutGrid, List, ChevronLeft, ChevronRight, GripVertical, Pencil, Image as ImageIcon } from "lucide-react";
import { DAILY_TASK_STATUSES, STATUS_LABEL, STATUS_TONE } from "@/lib/dailyTaskStatus";
import { Tag } from "@/components/Layout";
import CategorizedProjectSelect from "@/components/CategorizedProjectSelect";
import TaskEditorDialog from "@/components/TaskEditorDialog";
// Phase 84.10 — @dnd-kit for keyboard + touch drag-and-drop (a11y parity).
// Replaces the Phase 84.9 HTML5-native setData/getData implementation which
// couldn't be driven by a keyboard.
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors, useDraggable, useDroppable, pointerWithin, rectIntersection } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";

const MINE = "mine";
const ALL = "all";
const NO_PROJECT = "no_project";  // project filter sentinel
const STATUS_ALL = "all";
const VIEW_LIST = "list";      // Phase 84.9 — view toggle
const VIEW_WEEK = "week";

// Week columns start on Monday. UNSCHEDULED bucket collects tasks with no due_date.
// Exported so <TeamWeeklyBoard> (Phase 84.10) can reuse the same day-key math.
export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const UNSCHEDULED_KEY = "__unscheduled__";

// Return YYYY-MM-DD for a Date object in local time (no TZ shift).
export const toISODateLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// Monday of the week containing `date`. Monday=0, Sunday=6.
export const mondayOf = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();               // 0=Sun, 1=Mon...
  const shift = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + shift);
  return d;
};

// Feb 2026 · fix drag-to-reschedule (dashboard week Kanban).
// `closestCenter` misfired when combined with `DragOverlay` because the
// visible ghost tracked the pointer while collision detection kept scoring
// against the original card's transformed rect — the drop then landed on
// whichever column was geometrically closest to that stale rect (often the
// right-most column). Fix uses `pointerWithin` first (which reads the actual
// pointer position from the drag event) and falls back to `rectIntersection`
// when the pointer lands on a gutter between columns. This matches the
// visual overlay 1-to-1, so dragging Wed→Fri actually lands on Friday.
// Exported so <TeamWeeklyBoard> shares the same behaviour.
export const dashboardCollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) return pointerHits;
  return rectIntersection(args);
};

export default function DailyTasksWidget({ scopeProjectId = null, defaultAssignee = null }) {
  const { user } = useAuth();
  const can = usePermissions();
  const isAdmin = can("tasks_admin");
  // Phase 84.2 — founder is admin superset (Phase 79 parity).
  const canAssignOthers = can("tasks_admin") || can("tasks_assign");

  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  // Phase 84.34 — when scopeProjectId is set (e.g. embedded inside a project
  // view), pin the project filter to that ID and hide the project dropdown.
  // defaultAssignee lets callers pre-select "All" so everyone's work is visible.
  const [assigneeFilter, setAssigneeFilter] = useState(defaultAssignee || MINE);
  const [projectFilter, setProjectFilter] = useState(scopeProjectId || ALL);
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);   // Phase 82
  // Phase 84.9 — list | week. Phase 84.10 — persist choice to localStorage
  // so users who prefer Kanban aren't reset on reload.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem("dailyTasksViewMode");
      return v === VIEW_WEEK || v === VIEW_LIST ? v : VIEW_LIST;
    } catch { return VIEW_LIST; }
  });
  useEffect(() => {
    try { localStorage.setItem("dailyTasksViewMode", viewMode); } catch { /* ignore quota / privacy mode */ }
  }, [viewMode]);
  const [weekOffset, setWeekOffset] = useState(0);                // 0 = this week, -1 = last, +1 = next
  const [tasks, setTasks] = useState(null);
  const [draft, setDraft] = useState("");
  const [draftAssignee, setDraftAssignee] = useState("");
  const [draftProject, setDraftProject] = useState("");
  const [draftDueDate, setDraftDueDate] = useState(() => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));  // Phase 101 — due_date is mandatory at the API boundary
  const [busy, setBusy] = useState(false);
  const [openTimePopoverFor, setOpenTimePopoverFor] = useState(null);
  // Feb 2026 · fix — assignee filter chips must NOT show teammates who have
  // zero tasks. Prior behaviour listed every internal user regardless of
  // whether they had a single daily task, causing the endless empty-column
  // frustration on the dashboard. We now keep a lightweight Set of the
  // user_ids that own ≥1 task (respecting the active project filter) and
  // gate chip rendering on membership. Set is refreshed alongside `load()`
  // so every add / delete / drag immediately re-computes chip visibility.
  const [taskAssignees, setTaskAssignees] = useState(null);

  // Hydrate team + project pickers.
  useEffect(() => {
    api.get("/users?kind=internal")
      .then(({ data }) => setTeam((data || []).filter((u) => u.id !== user?.id)))
      .catch(() => setTeam([]));
    api.get("/daily-tasks/projects")
      .then(({ data }) => setProjects(data || []))
      .catch(() => setProjects([]));
  }, [user?.id]);

  const load = useCallback(async () => {
    setTasks(null);
    try {
      const params = new URLSearchParams({ assignee_id: assigneeFilter });
      if (projectFilter && projectFilter !== ALL) params.set("project_id", projectFilter);
      const { data } = await api.get(`/daily-tasks?${params.toString()}`);
      setTasks(data || []);
      // Refresh chip-visibility set. When the user is already on the "All"
      // view we can reuse the payload we just fetched — otherwise we run a
      // side call to `?assignee_id=all` so chips still reflect global truth
      // (a "Mine" view shouldn't hide chips for other people with work).
      if (assigneeFilter === ALL) {
        setTaskAssignees(new Set((data || []).map((t) => t.assignee_id).filter(Boolean)));
      } else {
        const chipParams = new URLSearchParams({ assignee_id: ALL });
        if (projectFilter && projectFilter !== ALL) chipParams.set("project_id", projectFilter);
        api.get(`/daily-tasks?${chipParams.toString()}`)
          .then(({ data: all }) => setTaskAssignees(new Set((all || []).map((t) => t.assignee_id).filter(Boolean))))
          .catch(() => { /* keep last known set on transient failure */ });
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setTasks([]);
    }
  }, [assigneeFilter, projectFilter]);

  useEffect(() => { load(); }, [load]);

  const addTask = async () => {
    const t = draft.trim();
    if (!t) return;
    if (!draftDueDate) { toast.error("Due date is required"); return; }
    setBusy(true);
    try {
      const body = { title: t, due_date: draftDueDate };
      if (draftAssignee) body.assignee_id = draftAssignee;
      // Pre-fill from project filter if no explicit pick.
      const proj = draftProject || (projectFilter !== ALL ? projectFilter : "");
      if (proj) body.project_id = proj;
      await api.post("/daily-tasks", body);
      setDraft("");
      setDraftAssignee("");
      setDraftProject("");
      setDraftDueDate(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
      if (draftAssignee && assigneeFilter === MINE && draftAssignee !== user?.id) {
        setAssigneeFilter(ALL);
      } else {
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (t) => {
    // Phase 82.2 — preserve the prior non-done status when re-opening a
    // completed task. If a card was moved through in_progress → done and
    // then unchecked, it should return to in_progress, not silently reset
    // to planned. `previous_status` is stashed on the row every time we
    // patch it to "done".
    const next = t.status === "done" ? (t.previous_status || "planned") : "done";
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, status: next } : x));
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, { status: next });
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const setStatus = async (t, next) => {
    if (t.status === next) return;
    // Stash the current status as previous_status IF we're heading into
    // "done" so a later toggle-back can restore it.
    const patch = { status: next };
    if (next === "done" && t.status !== "done") patch.previous_status = t.status;
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, status: next } : x));
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, patch);
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/daily-tasks/${t.id}`);
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setRowProject = async (t, projectId) => {
    const body = { project_id: projectId || "" };
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, body);
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 84.2 — inline re-assign (admin/CM only). Backend already gates via
  // _can_assign_to_others; the UI hides the affordance for other roles so the
  // usual optimistic UX doesn't flash a permission error.
  const setRowAssignee = async (t, newAssigneeId) => {
    if (!newAssigneeId || newAssigneeId === t.assignee_id) return;
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, { assignee_id: newAssigneeId });
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
      toast.success(`Reassigned to ${data.assignee_name || "user"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 84.9 — Kanban week view: drag/click a card to change its due_date.
  // Pass "" (empty string) to clear the date and drop the card into
  // "Unscheduled". Optimistic like the other row helpers.
  const setRowDueDate = async (t, newDueDate) => {
    const patch = { due_date: newDueDate || "" };
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, due_date: newDueDate || null } : x));
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, patch);
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = useMemo(() => {
    if (!Array.isArray(tasks)) return { planned: 0, in_progress: 0, review: 0, done: 0, total: 0 };
    const c = { planned: 0, in_progress: 0, review: 0, done: 0, total: 0 };
    for (const t of tasks) {
      const s = DAILY_TASK_STATUSES.includes(t.status) ? t.status : "planned";
      c[s] += 1;
      c.total += 1;
    }
    return c;
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return tasks;
    if (statusFilter === STATUS_ALL) return tasks;
    return tasks.filter((t) => (t.status || "planned") === statusFilter);
  }, [tasks, statusFilter]);

  const renderPill = (current, value, label, onClick, testId) => {
    const active = current === value;
    return (
      <button
        key={value}
        onClick={onClick}
        className={`text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        data-testid={testId}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="card-flat overflow-hidden" data-testid="daily-tasks-widget">
      {/* Header — Phase 84.33 · project + assignee + status filters all inline
          so we save two rows of vertical space and give tasks more room. */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <ListChecks size={14} className="text-[#F54800]" />
          <div className="font-display font-semibold text-slate-900 text-sm">Daily tasks</div>
          <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm" data-testid="daily-tasks-count">
            {counts.total} total · {counts.done} done · {counts.blocked} blocked
          </span>
          {/* Project filter — dropdown (Phase 84.33), Phase 99 grouped by
              Category + active-projects-only. Only mounts when there are
              projects worth filtering by. Hidden when the widget is
              embedded inside a specific project view. */}
          {!scopeProjectId && projects.length > 0 && (
            <CategorizedProjectSelect
              projects={projects}
              value={projectFilter}
              onChange={setProjectFilter}
              topOption={{ value: ALL, label: "All projects" }}
              className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white w-auto max-w-[170px]"
              testId="daily-tasks-project-filter"
              keepProjectId={projectFilter !== ALL ? projectFilter : null}
            />
          )}
          {/* Assignee filter — dropdown (Phase 84.33). Mine/All + one entry
              per teammate with at least one task in scope. */}
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white px-1.5 pr-6 max-w-[160px]"
            data-testid="daily-tasks-assignee-filter"
            title="Filter by person"
          >
            <option value={MINE}>Mine</option>
            <option value={ALL}>All people</option>
            {team
              .filter((u) => {
                if (u.id === assigneeFilter) return true;
                if (taskAssignees === null) return false;
                return taskAssignees.has(u.id);
              })
              .map((u) => (
                <option key={u.id} value={u.id} data-testid={`daily-tasks-assignee-filter-opt-${u.id}`}>
                  {u.name || u.email}
                </option>
              ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Phase 84.9 — View toggle: list ↔ week Kanban */}
          <div className="inline-flex items-center gap-0.5 border border-slate-200 rounded-sm p-0.5" data-testid="daily-tasks-view-toggle">
            <button
              onClick={() => setViewMode(VIEW_LIST)}
              className={`p-1 rounded-sm text-[11px] inline-flex items-center gap-1 ${viewMode === VIEW_LIST ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="daily-tasks-view-list"
              title="List view"
            >
              <List size={11} />
            </button>
            <button
              onClick={() => setViewMode(VIEW_WEEK)}
              className={`p-1 rounded-sm text-[11px] inline-flex items-center gap-1 ${viewMode === VIEW_WEEK ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="daily-tasks-view-week"
              title="Week Kanban view"
            >
              <LayoutGrid size={11} />
            </button>
          </div>
          <Filter size={11} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white px-1.5 pr-6"
            data-testid="daily-tasks-status-filter"
            title="Filter by status"
          >
            <option value={STATUS_ALL}>All statuses</option>
            {DAILY_TASK_STATUSES.map((s) => (
              <option key={s} value={s} data-testid={`daily-tasks-status-filter-opt-${s}`}>
                {STATUS_LABEL[s]} ({counts[s]})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Phase 84.33 — project + assignee chip rows removed; the header
          dropdowns above replace them. */}

      {/* Create row */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5 flex-wrap">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
          placeholder="Add a task…"
          className="h-8 rounded-sm text-sm flex-1 min-w-[160px]"
          data-testid="daily-tasks-new-title"
        />
        {projects.length > 0 && (
          <CategorizedProjectSelect
            projects={projects}
            value={draftProject || "none"}
            onChange={(v) => setDraftProject(v === "none" ? "" : v)}
            topOption={{ value: "none", label: "No project" }}
            className="h-8 text-sm rounded-sm border border-slate-200 bg-white w-auto max-w-[170px]"
            testId="daily-tasks-new-project"
          />
        )}
        {canAssignOthers && (
          <select
            value={draftAssignee}
            onChange={(e) => setDraftAssignee(e.target.value)}
            className="h-8 text-sm rounded-sm border border-slate-200 px-2 bg-white max-w-[160px]"
            data-testid="daily-tasks-new-assignee"
            title="Assign to (defaults to yourself)"
          >
            <option value="">Self</option>
            {team.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
        )}
        <Input
          type="date"
          value={draftDueDate}
          onChange={(e) => setDraftDueDate(e.target.value)}
          className="h-8 rounded-sm text-sm max-w-[130px]"
          data-testid="daily-tasks-new-due"
          title="Due date (required)"
        />
        <Button
          size="sm"
          onClick={addTask}
          disabled={busy || !draft.trim() || !draftDueDate}
          className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
          data-testid="daily-tasks-add-btn"
        >
          {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />} Add
        </Button>
      </div>

      {/* List OR week Kanban (Phase 84.9) */}
      {viewMode === VIEW_LIST ? (
        <div className="max-h-[420px] overflow-y-auto" data-testid="daily-tasks-list">
          {tasks === null && (
            <div className="px-4 py-6 text-xs text-slate-400 italic">Loading…</div>
          )}
          {tasks && filteredTasks.length === 0 && (
            <div className="px-4 py-6 text-xs text-slate-400 italic text-center" data-testid="daily-tasks-empty">
              {statusFilter === STATUS_ALL ? "No tasks yet. Add one above." : `No tasks with status "${STATUS_LABEL[statusFilter]}".`}
            </div>
          )}
          {tasks && filteredTasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              projects={projects}
              team={team}
              canAssignOthers={canAssignOthers}
              showAssignee={assigneeFilter !== MINE}
              canRemove={t.created_by === user?.id || isAdmin}
              timePopoverOpen={openTimePopoverFor === t.id}
              onTogglePopover={(open) => setOpenTimePopoverFor(open ? t.id : null)}
              onToggle={() => toggle(t)}
              onStatusChange={(s) => setStatus(t, s)}
              onRemove={() => remove(t)}
              onProjectChange={(pid) => setRowProject(t, pid)}
              onAssigneeChange={(uid) => setRowAssignee(t, uid)}
              onTimeLogged={(updated) => setTasks((arr) => arr.map((x) => x.id === updated.id ? updated : x))}
            />
          ))}
        </div>
      ) : (
        <WeekKanban
          tasks={filteredTasks || []}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
          showAssignee={assigneeFilter !== MINE}
          onStatusChange={setStatus}
          onToggle={toggle}
          onDueDateChange={setRowDueDate}
          onReload={load}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Single row                                                                 */
/* -------------------------------------------------------------------------- */
function TaskRow({
  task: t, projects, team, canAssignOthers, showAssignee, canRemove,
  timePopoverOpen, onTogglePopover,
  onToggle, onStatusChange, onRemove, onProjectChange, onAssigneeChange, onTimeLogged,
}) {
  const done = t.status === "done";
  const status = DAILY_TASK_STATUSES.includes(t.status) ? t.status : "planned";
  const hours = (t.total_minutes || 0) / 60;
  const hoursLabel = hours >= 1 ? `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}h` : (t.total_minutes ? `${t.total_minutes}m` : "0h");

  return (
    <div
      className="px-4 py-2 flex items-center gap-2 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 group relative"
      data-testid={`daily-task-row-${t.id}`}
    >
      <button
        onClick={onToggle}
        className={`shrink-0 p-1 rounded-sm ${done ? "text-emerald-600" : "text-slate-300 hover:text-slate-600"}`}
        data-testid={`daily-task-toggle-${t.id}`}
        title={done ? "Mark as not done" : "Mark as done"}
      >
        {done ? <CheckSquare size={15} /> : <Square size={15} />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`text-sm ${done ? "line-through text-slate-400" : "text-slate-800"} flex items-center gap-1`}>
          {/* Phase 84.34 — camera icon marks tasks that mirror a marketing draft. */}
          {t.linked_draft_id && (
            <ImageIcon
              size={11}
              className="text-pink-500 shrink-0"
              data-testid={`daily-task-draft-icon-${t.id}`}
            />
          )}
          <span className="truncate">{t.title}</span>
        </div>
        {(showAssignee || t.project_name) && (
          <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap" data-testid={`daily-task-meta-${t.id}`}>
            {showAssignee && t.assignee_name && (
              <span data-testid={`daily-task-assignee-${t.id}`}>{t.assignee_name}</span>
            )}
            {t.project_name && (
              <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm" data-testid={`daily-task-project-name-${t.id}`}>
                {t.project_name}
              </span>
            )}
          </div>
        )}
      </div>
      {/* Phase 82 — Status column: shows current status + click to change */}
      <select
        value={status}
        onChange={(e) => onStatusChange(e.target.value)}
        className={`h-7 text-[10px] rounded-sm border bg-white px-1.5 pr-5 shrink-0 font-medium ${
          status === "done" ? "border-emerald-300 text-emerald-700" :
          status === "review" ? "border-purple-300 text-purple-700" :
          status === "in_progress" ? "border-blue-300 text-blue-700" :
          "border-slate-300 text-slate-600"
        }`}
        data-testid={`daily-task-status-${t.id}`}
        title="Change status"
      >
        {DAILY_TASK_STATUSES.map((s) => (
          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
        ))}
      </select>
      {/* Project chip dropdown — Phase 99 grouped + active-only, but keeps
          this task's own project visible even if it's since gone inactive. */}
      <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <CategorizedProjectSelect
          projects={projects}
          value={t.project_id || "none"}
          onChange={(v) => onProjectChange(v === "none" ? "" : v)}
          topOption={{ value: "none", label: "No project" }}
          className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white w-auto max-w-[120px]"
          testId={`daily-task-project-chip-${t.id}`}
          keepProjectId={t.project_id}
        />
      </div>
      {/* Phase 84.2 — Assignee chip dropdown (admin/CM only) */}
      {canAssignOthers && (
        <select
          value={t.assignee_id || ""}
          onChange={(e) => onAssigneeChange(e.target.value)}
          className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white max-w-[120px] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          data-testid={`daily-task-assignee-chip-${t.id}`}
          title="Re-assign to someone else"
        >
          {/* Keep current assignee visible even if they're not in `team` (which excludes self) */}
          {t.assignee_id && !team.find((u) => u.id === t.assignee_id) && (
            <option value={t.assignee_id}>{t.assignee_name || "Current"}</option>
          )}
          {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      )}
      {/* Hours chip — click to open time popover */}
      <button
        onClick={() => onTogglePopover(!timePopoverOpen)}
        disabled={!t.project_id}
        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1 shrink-0 ${t.project_id ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-slate-50 text-slate-300 cursor-not-allowed"}`}
        data-testid={`daily-task-hours-chip-${t.id}`}
        title={t.project_id ? "Log time → adds to your timesheet" : "Attach a project before logging time"}
      >
        <Clock size={10} /> {hoursLabel}
      </button>
      {timePopoverOpen && (
        <TimeLogPopover
          task={t}
          onClose={() => onTogglePopover(false)}
          onLogged={(updated) => { onTimeLogged(updated); onTogglePopover(false); }}
        />
      )}
      {canRemove && (
        <button
          onClick={onRemove}
          className="text-slate-300 hover:text-rose-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          data-testid={`daily-task-delete-${t.id}`}
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Inline time-entry popover                                                  */
/* -------------------------------------------------------------------------- */
function TimeLogPopover({ task, onClose, onLogged }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [hours, setHours] = useState("");
  const [date, setDate] = useState(todayIso);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [onClose]);

  const submit = async () => {
    const h = Number(hours);
    if (!h || h <= 0 || h > 24) { toast.error("Enter hours between 0 and 24"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/daily-tasks/${task.id}/log-time`, {
        hours: h, date, notes: notes || null,
      });
      toast.success(`Logged ${h}h on “${task.title}” → timesheet updated`);
      onLogged(data.task);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      ref={cardRef}
      className="absolute right-2 top-10 z-30 bg-white border border-slate-200 rounded-sm shadow-lg p-3 w-64 space-y-2"
      data-testid={`daily-task-time-popover-${task.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Log time → timesheet</div>
      <div className="text-[10px] text-slate-400 truncate">Project: <span className="text-slate-700">{task.project_name || "—"}</span></div>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          step="0.25"
          min="0"
          max="24"
          autoFocus
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder="Hours"
          className="h-8 rounded-sm text-sm flex-1"
          data-testid={`daily-task-time-hours-${task.id}`}
        />
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-8 rounded-sm text-sm flex-1"
          data-testid={`daily-task-time-date-${task.id}`}
        />
      </div>
      <Input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="h-8 rounded-sm text-sm"
        data-testid={`daily-task-time-notes-${task.id}`}
      />
      <div className="flex items-center justify-end gap-1.5">
        <Button size="sm" variant="outline" onClick={onClose} className="rounded-sm h-7 text-[11px]">Cancel</Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={busy || !hours}
          className="rounded-sm h-7 text-[11px] bg-[#F54800] hover:bg-[#D63E00] text-white"
          data-testid={`daily-task-time-submit-${task.id}`}
        >
          {busy ? <Loader2 size={10} className="mr-1 animate-spin" /> : null} Log time
        </Button>
      </div>
    </div>
  );
}

/* ============================================================================
 *  Phase 84.9 — Week Kanban view
 *  Phase 84.10 — migrated from HTML5 native DnD to @dnd-kit for keyboard +
 *  touch drag support (a11y parity for the Kanban board).
 *
 *  Renders 7 day-columns (Mon → Sun) for the visible week + an "Unscheduled"
 *  column for tasks that have no due_date. Prev/Next arrows shift the visible
 *  week. Today's column is highlighted. Cards can be reassigned to a
 *  different day via mouse, touch, OR keyboard (Space to pick up, arrow
 *  keys to navigate, Space again to drop).
 * ========================================================================== */
function WeekKanban({ tasks, weekOffset, setWeekOffset, showAssignee, onStatusChange, onToggle, onDueDateChange, onReload }) {
  // Build the 7 columns for the visible week, keyed to Monday.
  const monday = useMemo(() => {
    const m = mondayOf(new Date());
    m.setDate(m.getDate() + weekOffset * 7);
    return m;
  }, [weekOffset]);
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [monday]);
  const todayISO = toISODateLocal(new Date());

  // Group tasks by their due_date ISO (YYYY-MM-DD). Anything without a
  // due_date lands in the UNSCHEDULED bucket so it stays visible.
  const byDay = useMemo(() => {
    const m = { [UNSCHEDULED_KEY]: [] };
    for (const d of days) m[toISODateLocal(d)] = [];
    for (const t of (tasks || [])) {
      const iso = t.due_date && /^\d{4}-\d{2}-\d{2}/.test(t.due_date) ? t.due_date.slice(0, 10) : null;
      if (iso && m[iso]) {
        m[iso].push(t);
      } else if (!iso) {
        m[UNSCHEDULED_KEY].push(t);
      }
      // Tasks with a due_date OUTSIDE the visible week are intentionally
      // hidden. The "N more outside this week" hint below surfaces the count.
    }
    // Phase 84.28 — Done cards ALWAYS sink to the bottom of every column so
    // active work sits above the fold. Stable sort preserves the user's
    // manual ordering within each group.
    for (const k of Object.keys(m)) {
      m[k] = [...m[k]].sort((a, b) => (a.status === "done" ? 1 : 0) - (b.status === "done" ? 1 : 0));
    }
    return m;
  }, [tasks, days]);

  const weekRangeLabel = useMemo(() => {
    const first = days[0]; const last = days[6];
    const opts = { day: "numeric", month: "short" };
    return `${first.toLocaleDateString(undefined, opts)} – ${last.toLocaleDateString(undefined, { ...opts, year: "numeric" })}`;
  }, [days]);

  const outsideWeekCount = useMemo(() => {
    if (!tasks) return 0;
    const inWeek = new Set(days.map(toISODateLocal));
    let n = 0;
    for (const t of tasks) {
      const iso = t.due_date && /^\d{4}-\d{2}-\d{2}/.test(t.due_date) ? t.due_date.slice(0, 10) : null;
      if (iso && !inWeek.has(iso)) n += 1;
    }
    return n;
  }, [tasks, days]);

  // @dnd-kit sensors: mouse+touch via PointerSensor (small activation
  // distance so clicks on the status Select don't accidentally drag),
  // keyboard via KeyboardSensor with sortable coordinate getter.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [activeTask, setActiveTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);  // Phase 84.28 — full edit modal target

  const handleDragStart = (e) => {
    const id = e.active?.id;
    const t = (tasks || []).find((x) => x.id === id);
    setActiveTask(t || null);
  };
  const handleDragEnd = (e) => {
    setActiveTask(null);
    const id = e.active?.id;
    const targetKey = e.over?.id;
    if (!id || !targetKey) return;
    const task = (tasks || []).find((t) => t.id === id);
    if (!task) return;
    const newDate = targetKey === UNSCHEDULED_KEY ? "" : String(targetKey);
    const currentIso = task.due_date && /^\d{4}-\d{2}-\d{2}/.test(task.due_date) ? task.due_date.slice(0, 10) : "";
    if (currentIso === newDate) return;
    onDueDateChange(task, newDate);
  };

  return (
    <div className="p-3" data-testid="daily-tasks-week-kanban">
      {/* Week navigation */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekOffset(weekOffset - 1)} className="p-1 rounded-sm hover:bg-slate-100" data-testid="week-kanban-prev" title="Previous week">
            <ChevronLeft size={14} />
          </button>
          <button onClick={() => setWeekOffset(0)} className="text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 hover:bg-slate-200" data-testid="week-kanban-today" title="Jump to this week">
            {weekOffset === 0 ? "This week" : "Today"}
          </button>
          <button onClick={() => setWeekOffset(weekOffset + 1)} className="p-1 rounded-sm hover:bg-slate-100" data-testid="week-kanban-next" title="Next week">
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium" data-testid="week-kanban-range">
          {weekRangeLabel}
        </div>
        {outsideWeekCount > 0 && (
          <div className="text-[10px] text-slate-400 italic" title="Tasks whose due_date falls outside this week — navigate via prev/next.">
            {outsideWeekCount} more outside this week
          </div>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={dashboardCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* Columns */}
        <div className="grid grid-cols-8 gap-2 overflow-x-auto">
          {/* Unscheduled column first */}
          <KanbanColumn
            columnKey={UNSCHEDULED_KEY}
            label="Unscheduled"
            sub="No due date"
            isToday={false}
            tasks={byDay[UNSCHEDULED_KEY]}
            showAssignee={showAssignee}
            onStatusChange={onStatusChange}
            onToggle={onToggle}
            onEdit={setEditingTask}
          />
          {days.map((d, i) => {
            const iso = toISODateLocal(d);
            return (
              <KanbanColumn
                key={iso}
                columnKey={iso}
                label={DAY_LABELS[i]}
                sub={d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                isToday={iso === todayISO}
                tasks={byDay[iso]}
                showAssignee={showAssignee}
                onStatusChange={onStatusChange}
                onToggle={onToggle}
                onEdit={setEditingTask}
              />
            );
          })}
        </div>
        {/* DragOverlay renders a lightweight ghost of the picked-up card. */}
        <DragOverlay dropAnimation={null}>
          {activeTask ? <KanbanCard task={activeTask} showAssignee={showAssignee} onStatusChange={() => {}} onToggle={() => {}} isOverlay /> : null}
        </DragOverlay>
      </DndContext>
      <div className="mt-2 text-[10px] text-slate-400 italic">Tip · drag a card into another day to change its due date. Keyboard: focus the drag handle → Space → arrow keys → Space.</div>

      {/* Phase 84.28 — full task edit modal wired to every KanbanCard's Pencil button. */}
      <TaskEditorDialog
        open={!!editingTask}
        task={editingTask}
        onOpenChange={(v) => !v && setEditingTask(null)}
        onSaved={() => { setEditingTask(null); onReload && onReload(); }}
        onDeleted={() => { setEditingTask(null); onReload && onReload(); }}
      />
    </div>
  );
}

function KanbanColumn({ columnKey, label, sub, isToday, tasks, showAssignee, onStatusChange, onToggle, onEdit }) {
  // Phase 84.10 — droppable via @dnd-kit useDroppable hook.
  const { isOver, setNodeRef } = useDroppable({ id: columnKey });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-sm border ${isToday ? "border-[#F54800] bg-orange-50/40" : "border-slate-200 bg-slate-50/30"} min-h-[240px] flex flex-col`}
      data-testid={`week-kanban-col-${columnKey}`}
    >
      <div className={`px-2 py-1.5 border-b ${isToday ? "border-[#F54800]/30" : "border-slate-200"} flex items-center justify-between`}>
        <div>
          <div className={`text-[11px] font-semibold uppercase tracking-wider ${isToday ? "text-[#F54800]" : "text-slate-700"}`}>{label}</div>
          <div className="text-[9px] text-slate-500">{sub}</div>
        </div>
        <div className="text-[10px] text-slate-500 bg-white border border-slate-200 px-1.5 rounded-sm" data-testid={`week-kanban-count-${columnKey}`}>
          {tasks.length}
        </div>
      </div>
      <div className={`flex-1 p-1.5 space-y-1.5 ${isOver ? "bg-blue-50/40" : ""}`}>
        {tasks.length === 0 ? (
          <div className="text-[10px] text-slate-300 italic text-center py-4">Drop here</div>
        ) : (
          tasks.map((t) => (
            <KanbanCard
              key={t.id}
              task={t}
              showAssignee={showAssignee}
              onStatusChange={(s) => onStatusChange(t, s)}
              onToggle={() => onToggle(t)}
              onEdit={onEdit}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function KanbanCard({ task: t, showAssignee, onStatusChange, onToggle, onEdit, isOverlay = false }) {
  // Phase 84.10 — draggable via @dnd-kit useDraggable hook. The drag handle
  // is scoped to a small grip icon so clicking the status Select / checkbox
  // doesn't accidentally initiate a drag on touch devices.
  //
  // Feb 2026 fix — DO NOT apply the `transform` style to the original
  // draggable when a <DragOverlay> is in use. Doing so lets the original
  // card visually chase the pointer *and* mutates its bounding rect, which
  // in turn poisons collision detection (drops would land on whichever
  // column was closest to the moved rect, not to the pointer). @dnd-kit's
  // own docs are explicit: "When using a DragOverlay, don't apply the
  // transform property to your draggable node style." We now just fade the
  // in-place card and let the overlay be the visible drag ghost.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: t.id,
    disabled: isOverlay,   // ghost inside <DragOverlay> must not be draggable itself
  });
  const style = isDragging ? { opacity: 0.4 } : undefined;

  const done = t.status === "done";
  const status = DAILY_TASK_STATUSES.includes(t.status) ? t.status : "planned";
  const hours = (t.total_minutes || 0) / 60;
  const hoursLabel = hours >= 1 ? `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}h` : (t.total_minutes ? `${t.total_minutes}m` : "");
  const statusColor = status === "done" ? "border-l-emerald-500" :
                       status === "blocked" ? "border-l-amber-500" :
                       status === "in_progress" ? "border-l-blue-500" :
                       "border-l-slate-300";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-slate-200 border-l-2 ${statusColor} rounded-sm p-1.5 shadow-sm hover:border-slate-300 transition-shadow`}
      data-testid={`week-kanban-card-${t.id}`}
    >
      <div className="flex items-start gap-1">
        {/* Dedicated drag handle — keeps the rest of the card clickable. */}
        <button
          {...listeners}
          {...attributes}
          className="shrink-0 p-0.5 text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing focus-visible:ring-2 focus-visible:ring-blue-400 rounded-sm"
          data-testid={`week-kanban-drag-${t.id}`}
          aria-label={`Drag task: ${t.title}`}
          title="Drag to a different day"
          onClick={(e) => e.preventDefault()}
        >
          <GripVertical size={11} />
        </button>
        <button
          onClick={onToggle}
          className={`shrink-0 p-0.5 ${done ? "text-emerald-600" : "text-slate-300 hover:text-slate-600"}`}
          data-testid={`week-kanban-toggle-${t.id}`}
          title={done ? "Mark as not done" : "Mark as done"}
        >
          {done ? <CheckSquare size={12} /> : <Square size={12} />}
        </button>
        <div className={`text-[11px] leading-snug ${done ? "line-through text-slate-400" : "text-slate-800"} flex-1 break-words`}>
          {/* Phase 84.34 — camera icon marks tasks that mirror a marketing draft. */}
          {t.linked_draft_id && (
            <ImageIcon
              size={10}
              className="inline-block mr-1 text-pink-500 shrink-0"
              data-testid={`week-kanban-draft-icon-${t.id}`}
            />
          )}
          {t.title}
        </div>
        {/* Phase 84.28 — Pencil opens the full edit modal (description, due date, reassign, image, delete). */}
        {onEdit && !isOverlay && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(t); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="shrink-0 p-0.5 text-slate-300 hover:text-[#F54800]"
            data-testid={`week-kanban-edit-${t.id}`}
            title="Edit task"
          >
            <Pencil size={11} />
          </button>
        )}
      </div>
      {/* Description + image inline preview, muted so the card stays compact. */}
      {t.image_url && (
        <img src={t.image_url} alt="" className="mt-1 w-full max-h-24 object-cover rounded-sm border border-slate-200" data-testid={`week-kanban-image-${t.id}`} />
      )}
      {(showAssignee || t.project_name) && (
        <div className="text-[9px] text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
          {showAssignee && t.assignee_name && (
            <span className="truncate max-w-[80px]" title={t.assignee_name} data-testid={`week-kanban-assignee-${t.id}`}>{t.assignee_name}</span>
          )}
          {t.project_name && (
            <span className="bg-slate-100 text-slate-600 px-1 rounded-sm truncate max-w-[90px]" title={t.project_name}>{t.project_name}</span>
          )}
        </div>
      )}
      <div className="mt-1 flex items-center gap-1 flex-wrap">
        <select
          value={status}
          onChange={(e) => { e.stopPropagation(); onStatusChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}  /* prevent PointerSensor drag on select */
          className={`h-5 text-[9px] rounded-sm border bg-white px-1 pr-4 font-medium ${
            status === "done" ? "border-emerald-300 text-emerald-700" :
            status === "blocked" ? "border-amber-300 text-amber-700" :
            status === "in_progress" ? "border-blue-300 text-blue-700" :
            "border-slate-300 text-slate-600"
          }`}
          data-testid={`week-kanban-status-${t.id}`}
          title="Change status"
        >
          {DAILY_TASK_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
        {hoursLabel && (
          <span className="text-[9px] text-slate-500 bg-slate-100 px-1 rounded-sm inline-flex items-center gap-0.5" title="Total time logged">
            <Clock size={8} /> {hoursLabel}
          </span>
        )}
      </div>
    </div>
  );
}

